import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface ColumnWidthConfig {
  id: string;
  /** Proportional width weight (not necessarily on-screen pixels). */
  defaultWidth: number;
  minWidth: number;
  resizable: boolean;
}

export interface ColumnWidthInput {
  id: string;
  defaultWidth?: number;
  minWidth?: number;
  resizable?: boolean;
}

/** Default proportional weight when a column omits defaultWidth. */
export const FALLBACK_COLUMN_WIDTH = 150;
export const FALLBACK_COLUMN_MIN_WIDTH = 72;

export function resolveColumnWidthConfigs(
  columns: ColumnWidthInput[],
): ColumnWidthConfig[] {
  return columns.map((column) => ({
    id: column.id,
    defaultWidth: column.defaultWidth ?? FALLBACK_COLUMN_WIDTH,
    minWidth: column.minWidth ?? FALLBACK_COLUMN_MIN_WIDTH,
    resizable: column.resizable !== false,
  }));
}

function buildWidthMap(configs: ColumnWidthConfig[]): Record<string, number> {
  const next: Record<string, number> = {};
  for (const config of configs) {
    next[config.id] = config.defaultWidth;
  }
  return next;
}

function clampWeight(value: number, config: ColumnWidthConfig): number {
  return Math.max(config.minWidth, value);
}

function resolveNeighbor(
  configs: ColumnWidthConfig[],
  columnId: string,
): ColumnWidthConfig | null {
  const index = configs.findIndex((item) => item.id === columnId);
  if (index < 0) {
    return null;
  }
  const neighbor = configs[index + 1] ?? configs[index - 1] ?? null;
  if (!neighbor || neighbor.id === columnId) {
    return null;
  }
  return neighbor;
}

function applyResizeDelta(
  configs: ColumnWidthConfig[],
  widths: Record<string, number>,
  columnId: string,
  neighborId: string,
  startWeight: number,
  neighborStartWeight: number,
  deltaWeight: number,
): Record<string, number> | null {
  const active = configs.find((item) => item.id === columnId);
  const donor = configs.find((item) => item.id === neighborId);
  if (!active || !donor) {
    return null;
  }

  const unclampedActive = startWeight + deltaWeight;
  const nextActive = clampWeight(unclampedActive, active);
  const appliedDelta = nextActive - startWeight;

  const unclampedNeighbor = neighborStartWeight - appliedDelta;
  const nextNeighbor = clampWeight(unclampedNeighbor, donor);
  const neighborDelta = neighborStartWeight - nextNeighbor;
  const finalActive = clampWeight(startWeight + neighborDelta, active);

  return {
    ...widths,
    [columnId]: finalActive,
    [neighborId]: nextNeighbor,
  };
}

/**
 * Column widths are proportional weights that always sum to a constant while
 * dragging: delta is stolen from the neighboring column so totalWeight (and
 * thus % layout) keeps 1px mouse ≈ 1px on screen.
 */
export function useColumnResize(configs: ColumnWidthConfig[]) {
  const configKey = useMemo(
    () =>
      configs
        .map(
          (config) =>
            `${config.id}:${config.defaultWidth}:${config.minWidth}:${config.resizable}`,
        )
        .join("|"),
    [configs],
  );

  const configsRef = useRef(configs);
  configsRef.current = configs;

  const [widths, setWidths] = useState<Record<string, number>>(() =>
    buildWidthMap(configs),
  );

  const widthsRef = useRef(widths);
  widthsRef.current = widths;

  // Reset only when column width contract changes — not on array identity.
  useEffect(() => {
    setWidths(buildWidthMap(configsRef.current));
  }, [configKey]);

  const dragRef = useRef<{
    columnId: string;
    neighborId: string;
    startX: number;
    startWeight: number;
    neighborStartWeight: number;
    renderedWidthPx: number;
  } | null>(null);

  const detachListenersRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      detachListenersRef.current?.();
      detachListenersRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, []);

  const nudgeColumn = useCallback(
    (columnId: string, deltaPx: number, headerElement: HTMLElement) => {
      const currentConfigs = configsRef.current;
      const config = currentConfigs.find((item) => item.id === columnId);
      if (!config?.resizable) {
        return;
      }

      const neighbor = resolveNeighbor(currentConfigs, columnId);
      if (!neighbor) {
        return;
      }

      const renderedWidthPx = headerElement.getBoundingClientRect().width;
      if (renderedWidthPx <= 0) {
        return;
      }

      const currentWidths = widthsRef.current;
      const startWeight = currentWidths[columnId] ?? config.defaultWidth;
      const neighborStartWeight =
        currentWidths[neighbor.id] ?? neighbor.defaultWidth;
      const deltaWeight = deltaPx * (startWeight / renderedWidthPx);

      const next = applyResizeDelta(
        currentConfigs,
        currentWidths,
        columnId,
        neighbor.id,
        startWeight,
        neighborStartWeight,
        deltaWeight,
      );
      if (next) {
        setWidths(next);
      }
    },
    [],
  );

  const startResize = useCallback(
    (
      columnId: string,
      clientX: number,
      headerElement: HTMLElement,
      handleElement: HTMLElement,
      pointerId: number,
    ) => {
      const currentConfigs = configsRef.current;
      const config = currentConfigs.find((item) => item.id === columnId);
      if (!config?.resizable) {
        return;
      }

      const renderedWidthPx = headerElement.getBoundingClientRect().width;
      if (renderedWidthPx <= 0) {
        return;
      }

      const neighbor = resolveNeighbor(currentConfigs, columnId);
      if (!neighbor) {
        return;
      }

      detachListenersRef.current?.();

      const currentWidths = widthsRef.current;
      dragRef.current = {
        columnId,
        neighborId: neighbor.id,
        startX: clientX,
        startWeight: currentWidths[columnId] ?? config.defaultWidth,
        neighborStartWeight: currentWidths[neighbor.id] ?? neighbor.defaultWidth,
        renderedWidthPx,
      };
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      try {
        handleElement.setPointerCapture(pointerId);
      } catch {
        // Pointer capture may fail in some browsers; fall back to document listeners.
      }

      const finish = () => {
        dragRef.current = null;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        handleElement.removeEventListener("pointermove", onPointerMove);
        handleElement.removeEventListener("pointerup", finish);
        handleElement.removeEventListener("pointercancel", finish);
        handleElement.removeEventListener("lostpointercapture", finish);
        document.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("pointerup", finish);
        detachListenersRef.current = null;
      };

      const onPointerMove = (event: PointerEvent) => {
        if (event.buttons === 0) {
          finish();
          return;
        }

        const drag = dragRef.current;
        if (!drag) {
          return;
        }

        const latestConfigs = configsRef.current;
        const deltaWeight =
          (event.clientX - drag.startX) *
          (drag.startWeight / drag.renderedWidthPx);

        const next = applyResizeDelta(
          latestConfigs,
          widthsRef.current,
          drag.columnId,
          drag.neighborId,
          drag.startWeight,
          drag.neighborStartWeight,
          deltaWeight,
        );
        if (next) {
          setWidths(next);
        }
      };

      handleElement.addEventListener("pointermove", onPointerMove);
      handleElement.addEventListener("pointerup", finish);
      handleElement.addEventListener("pointercancel", finish);
      handleElement.addEventListener("lostpointercapture", finish);
      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("pointerup", finish);
      detachListenersRef.current = finish;
    },
    [],
  );

  return { widths, startResize, nudgeColumn };
}
